import { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import PanelHeader from "../../components/timetable/PanelHeader";
import {
  BTN,
  CARD,
  Card,
  EmptyState,
  Notice,
  Panel,
  STALE,
  TABLE_HEAD,
} from "../../components/timetable/PanelKit";
import { importTimetable } from "../../services/timetableService";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { parseCsv } from "../../utils/helpers/csv";
import Spinner from "../../components/Spinner/Spinner";

const TEMPLATE_CSV =
  "teacher_email,group_code,weekday,period,start_time\n" +
  "teacher@example.com,9B,1,1,08:30\n";

const MAX_CSV_BYTES = 2 * 1024 * 1024; // 2 MB — schedule exports are tiny


const OUTCOME_CLASS = {
  create: "bg-emerald-50 text-emerald-600",
  update: "bg-indigo-50 text-indigo-600",
  error: "bg-rose-50 text-rose-600",
};

// The report's filters, in the order they matter. A real school CSV is 800-1200
// rows and the only question anyone asks of the preview is "what is wrong with
// it?" — which, before this, meant scrolling twelve hundred green rows looking
// for red ones. Warnings are their own bucket rather than folded into errors:
// a warning row still imports, so it is a different decision.
const REPORT_FILTERS = [
  { key: "all", match: () => true },
  { key: "error", match: (row) => row.outcome === "error" },
  { key: "warning", match: (row) => (row.warnings || []).length > 0 },
  { key: "create", match: (row) => row.outcome === "create" },
  { key: "update", match: (row) => row.outcome === "update" },
];

export default function ScheduleImport() {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");
  const [reportFilter, setReportFilter] = useState("all");

  const reset = () => {
    setRows([]);
    setFileName("");
    setReport(null);
    setReportFilter("all");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_CSV_BYTES) {
      toast.error(t("schedule.fileTooLarge"));
      e.target.value = "";
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onerror = () => {
      toast.error(t("schedule.readError"));
      reset();
    };
    reader.onload = async () => {
      const parsed = parseCsv(String(reader.result || ""));
      setRows(parsed);
      setReport(null);
      if (parsed.length === 0) return;
      setBusy("preview");
      try {
        const result = await importTimetable({ rows: parsed, commit: false });
        setReport(result);
        // Land on the rows that need a decision. A clean file has none, so this
        // is a no-op exactly when there is nothing to look at.
        setReportFilter(result?.summary?.errors > 0 ? "error" : "all");
      } catch (err) {
        toast.error(formatApiError(err, t("schedule.lessonSaveFailed")));
      } finally {
        setBusy("");
      }
    };
    reader.readAsText(file);
  };

  const apply = async () => {
    setBusy("commit");
    try {
      const result = await importTimetable({ rows, commit: true });
      setReport(result);
      toast.success(
        t("schedule.summaryCommitted", {
          created: result.summary.created,
          updated: result.summary.updated,
        }),
      );
    } catch (err) {
      toast.error(formatApiError(err, t("schedule.lessonSaveFailed")));
    } finally {
      setBusy("");
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "timetable-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = report?.summary;
  const committed = Boolean(summary?.committed);
  const canApply = Boolean(summary && summary.total > 0 && summary.errors === 0 && !committed);
  const allErrors = Boolean(summary && summary.total > 0 && summary.errors === summary.total);
  const isPreviewing = busy === "preview";
  const isApplying = busy === "commit";

  // Counts on the chips, computed once for both the labels and the filtering.
  // A chip that says "Errors" without saying how many is a button you have to
  // press to find out whether it was worth pressing.
  const reportBuckets = useMemo(() => {
    const all = report?.rows || [];
    return REPORT_FILTERS.map((filter) => ({
      ...filter,
      rows: all.filter(filter.match),
    }));
  }, [report]);

  // The chips are hidden on a short file — so the filter must not apply there
  // either. Otherwise a six-row file with one error opens showing that one row
  // and no control anywhere to say why the other five are missing.
  const canFilterReport = (report?.rows || []).length > 8;
  const visibleReportRows = canFilterReport
    ? reportBuckets.find((bucket) => bucket.key === reportFilter)?.rows || []
    : report?.rows || [];

  // Page wrapper, back link, WIDTH and permission guard live on
  // TimetableWorkspace; rhythm and surfaces come from PanelKit. This panel used
  // to cap itself at `max-w-4xl` with no `mx-auto`, which left the report table
  // — the only wide thing on the page — scrolling horizontally inside 896px
  // while 256px of empty column sat beside it.
  return (
    <Panel>
      <PanelHeader
        title={t("schedule.importTitle")}
        subtitle={t("schedule.importSubtitle")}
      >
        <button onClick={downloadTemplate} className={BTN.secondary}>
          {t("schedule.downloadTemplate")}
        </button>
      </PanelHeader>

      {/* The uploader and the standing warning side by side: the uploader is
          one button and a filename, so on its own it was a full-width white box
          containing ~20 characters. */}
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className={`${CARD} space-y-4 p-6`}>
          <div className="flex flex-wrap items-center gap-4">
            <label
              aria-describedby="schedule-import-hint"
              className={`cursor-pointer focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 ${BTN.primary}`}
            >
              {t("schedule.chooseFile")}
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="sr-only" />
            </label>
            <span className="text-xs font-bold text-slate-500">{fileName || t("schedule.noFile")}</span>
            {(rows.length > 0 || report) && (
              <button
                onClick={reset}
                aria-label={t("schedule.resetAria")}
                className={`ml-auto ${BTN.quiet}`}
              >
                {t("schedule.reset")}
              </button>
            )}
          </div>
          <p id="schedule-import-hint" className="text-xs font-medium leading-relaxed text-slate-500">
            {t("schedule.hint")}
          </p>
        </div>

        {/* Stated HERE, permanently — not for the first time in the commit
            dialog, at the moment the rows are destroyed. Importing and
            generating both fill one timetable, and publishing a generated
            draft replaces the whole thing, imported rows included. */}
        <Notice>{t("schedule.replacedByGenerationNotice")}</Notice>
      </div>

      {/* Only the PREVIEW replaces the page with a spinner — at that point
          there is nothing yet to preserve. Applying used to do the same, which
          took the report off screen at exactly the moment the operator wanted
          to watch what was being written; it now stays put, dimmed, with the
          Apply button carrying the busy state. */}
      {isPreviewing && <Spinner label={t("schedule.checking")} className="py-10" />}

      {!busy && fileName && rows.length === 0 && (
        <EmptyState
          iconPath="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          title={t("schedule.stateEmpty")}
        />
      )}

      {!isPreviewing && summary && committed && (
        <Notice tone="emerald">
          <div className="flex items-start gap-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6 shrink-0 text-emerald-600"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <div>
              <p className="text-sm font-bold text-emerald-800">
                {t("schedule.summaryCommitted", { created: summary.created, updated: summary.updated })}
              </p>
              <button onClick={reset} className={`mt-4 ${BTN.secondary}`}>
                {t("schedule.importAnother")}
              </button>
            </div>
          </div>
        </Notice>
      )}

      {!isPreviewing && summary && (
        <Card padded={false} aria-busy={isApplying}>
          {!committed && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5">
              <p className="text-sm font-bold text-slate-700">
                {t("schedule.summaryPreview", {
                  created: summary.created,
                  updated: summary.updated,
                  errors: summary.errors,
                })}
              </p>
              {canApply && (
                <button onClick={apply} disabled={isApplying} className={BTN.primary}>
                  {isApplying ? t("schedule.applying") : t("schedule.apply")}
                </button>
              )}
            </div>
          )}

          {allErrors && (
            <div className="border-b border-rose-100 bg-rose-50 px-6 py-2.5 text-[11px] font-black text-rose-700">
              {t("schedule.stateAllErrors", { count: summary.total })}
            </div>
          )}

          {/* Chips rather than a <select>: there are five, they carry counts,
              and the counts are the report's summary — so the control doubles
              as the thing you read to know how the import went. Hidden on a
              short file, where the whole report already fits on screen. */}
          {canFilterReport && (
            <div
              role="group"
              aria-label={t("schedule.filterAria")}
              className="flex flex-wrap gap-1.5 border-b border-slate-100 px-5 py-3"
            >
              {reportBuckets.map((bucket) => {
                const active = bucket.key === reportFilter;
                return (
                  <button
                    key={bucket.key}
                    type="button"
                    aria-pressed={active}
                    disabled={bucket.rows.length === 0 && bucket.key !== "all"}
                    onClick={() => setReportFilter(bucket.key)}
                    className={`rounded-lg px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                      active
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {t(`schedule.filter.${bucket.key}`)}
                    <span className={active ? "ml-1.5 opacity-80" : "ml-1.5 text-slate-500"}>
                      {bucket.rows.length}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className={`overflow-x-auto ${isApplying ? STALE : ""}`}>
            <table className="w-full text-left text-sm">
              <thead className={TABLE_HEAD}>
                <tr>
                  <th className="px-4 py-3">{t("schedule.colLine")}</th>
                  <th className="px-4 py-3">{t("schedule.colTeacher")}</th>
                  <th className="px-4 py-3">{t("schedule.colGroup")}</th>
                  <th className="px-4 py-3 hidden md:table-cell">{t("schedule.colWeekday")}</th>
                  <th className="px-4 py-3 hidden md:table-cell">{t("schedule.colPeriod")}</th>
                  <th className="px-4 py-3 hidden md:table-cell">{t("schedule.colTime")}</th>
                  <th className="px-4 py-3">{t("schedule.colStatus")}</th>
                  <th className="px-4 py-3">{t("schedule.colDetail")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleReportRows.map((r) => (
                  <tr
                    key={r.line}
                    className={`border-t border-slate-100 ${
                      r.outcome === "error"
                        ? "bg-rose-50/60 border-l-4 border-l-rose-400"
                        : (r.warnings || []).length > 0
                          ? "bg-amber-50/60 border-l-4 border-l-amber-300"
                          : ""
                    }`}
                  >
                    <td className="px-4 py-2.5 font-bold text-slate-500">{r.line}</td>
                    <td className="px-4 py-2.5 font-bold text-slate-700">{r.teacher_email}</td>
                    <td className="px-4 py-2.5 text-slate-600">{r.group_code}</td>
                    <td className="px-4 py-2.5 text-slate-600 hidden md:table-cell">{r.weekday}</td>
                    <td className="px-4 py-2.5 text-slate-600 hidden md:table-cell">{r.period}</td>
                    <td className="px-4 py-2.5 text-slate-600 hidden md:table-cell">{r.start_time}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest ${OUTCOME_CLASS[r.outcome] || "bg-slate-100 text-slate-600"}`}
                      >
                        {OUTCOME_CLASS[r.outcome]
                          ? t(`schedule.outcome${r.outcome.charAt(0).toUpperCase()}${r.outcome.slice(1)}`)
                          : r.outcome || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs font-medium">
                      {(r.errors || []).length > 0 && <span className="text-rose-600">{r.errors.join("; ")}</span>}
                      {(r.warnings || []).length > 0 && (
                        <span className="text-amber-600">{r.warnings.join("; ")}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {visibleReportRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-xs font-bold text-slate-500"
                    >
                      {t("schedule.filterNoRows")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </Panel>
  );
}
